import React, { useState } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ActionButton from "../../../widgets/ActionButton";
import { gridDeleteSx } from "../../../widgets/gridActionSx";
import { toggleSelectorSx } from "../../../widgets/toggleSelectorSx";
import SectionTitle from "../../../widgets/SectionTitle";
import SystemChip from "../../../widgets/SystemChip";
import { AlertProps } from "@mui/material/Alert";
import {
  GET_COLOR_CLS,
  GET_COLOR_RELATIONSHIPS,
  ADD_COLOR_RELATIONSHIPS,
  DELETE_COLOR_RELATIONSHIPS,
  tokenOptions,
} from "../../../../backend/graphql";
import {
  GraphQLDataGrid,
  GraphQLGridParameters,
  GraphQLActionParams,
} from "../../../widgets/GraphQLDataGrid";
import { useQuery, useMutation } from "@apollo/client";
import { useAppSelector } from "../../../../app/hooks";
import { isReadonly } from "../../../../app/features/auth/authTypes";
import { useConfirm } from "material-ui-confirm";

type ColorItem = {
  id: string;
  system: string;
  color: string;
};

export type ColorRelationshipConfig = {
  relationshipType: string;
  labels: {
    addTitle: string;
    addButton: string;
    existingTitle: string;
    addSuccess: string;
    addError: string;
    deleteConfirm: (color1: string, color2: string) => string;
    deleteAction: string;
    deleteSuccess: string;
  };
};

const getColorLabel = (c: ColorItem) => `${c.system} ${c.color}`;

const sortColors = (colors: ColorItem[]) =>
  [...colors].sort(
    (a, b) =>
      a.system.localeCompare(b.system) || a.color.localeCompare(b.color),
  );


const COLOR_SYSTEMS = ["GIA", "Langerman", "Flat", "Melee"];

type ConfirmationFilter = "all" | "confirmed" | "unconfirmed";

const CONFIRMATION_FILTERS: Record<ConfirmationFilter, string> = {
  all: "",
  confirmed: " && confirmedByData == true",
  unconfirmed: " && confirmedByData == false",
};

const ColorRelationshipTab: React.FC<{ config: ColorRelationshipConfig }> = ({
  config,
}) => {
  const { relationshipType, labels } = config;
  const token = useAppSelector((state) => state.auth.token);
  const readonly = isReadonly(useAppSelector((state) => state.auth.role));
  const confirm = useConfirm();

  const [color, setColor] = useState<ColorItem | null>(null);
  const [relatedColors, setRelatedColors] = useState<ColorItem[]>([]);
  const [confirmationFilter, setConfirmationFilter] =
    useState<ConfirmationFilter>("all");
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const { data: colorsData, loading: colorsLoading } = useQuery(GET_COLOR_CLS, {
    ...tokenOptions(token),
  });

  const [addRelationships, { loading: adding }] = useMutation(
    ADD_COLOR_RELATIONSHIPS,
    {
      ...tokenOptions(token),
      refetchQueries: [GET_COLOR_RELATIONSHIPS],
    },
  );
  const [deleteRelationships] = useMutation(
    DELETE_COLOR_RELATIONSHIPS,
    tokenOptions(token),
  );

  const allColors: ColorItem[] = colorsData?.colorCls?.items ?? [];
  const sortedColors = sortColors(allColors);

  const { data: existingRelData } = useQuery(GET_COLOR_RELATIONSHIPS, {
    variables: {
      filter: `type == "${relationshipType}" && colorID == "${color?.id}"`,
    },
    skip: !color,
    ...tokenOptions(token),
  });

  const existingRelatedIds = new Set<string>(
    (existingRelData?.colorRelationships?.items ?? []).map(
      (r: any) => r.relatedColorID,
    ),
  );

  const handleAdd = async () => {
    if (!color || relatedColors.length === 0) return;
    try {
      const result = await addRelationships({
        variables: {
          type: relationshipType,
          colorID: color.id,
          relatedColorIDs: relatedColors.map((c) => c.id),
        },
      });
      if (result.data?.addColorRelationships?.success === false) {
        setSnackbar({ children: labels.addError, severity: "error" });
      } else {
        setColor(null);
        setRelatedColors([]);
        setSnackbar({ children: labels.addSuccess, severity: "success" });
      }
    } catch {
      setSnackbar({ children: labels.addError, severity: "error" });
    }
  };

  const handleDelete = async (row: any, refetch: () => void) => {
    const colorLabel = getColorLabel(row.color);
    const relatedColorLabel = getColorLabel(row.relatedColor);
    try {
      await confirm({
        description: labels.deleteConfirm(colorLabel, relatedColorLabel),
      });
      await deleteRelationships({
        variables: {
          type: relationshipType,
          colorID: row.colorID,
          relatedColorIDs: [row.relatedColorID],
        },
      });
      refetch();
      setSnackbar({ children: labels.deleteSuccess, severity: "success" });
    } catch {
      // confirm cancelled or mutation failed
    }
  };

  const gridParameters: GraphQLGridParameters = {
    graphqlQuery: GET_COLOR_RELATIONSHIPS,
    graphqlItemsPath: (data) => data.colorRelationships,
    graphqlFilter: `type == "${relationshipType}"${CONFIRMATION_FILTERS[confirmationFilter]}`,
    fields: [
      { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
      {
        field: "color.system",
        label: "Color System",
        width: 150,
        type: "singleSelect",
        valueOptions: COLOR_SYSTEMS,
        valueGetter: (params: any) => params.row.color.system,
        renderCell: (params: any) =>
          params.value ? <SystemChip system={params.value} /> : null,
      },
      {
        field: "color.color",
        label: "Color",
        flex: 1,
        valueGetter: (params: any) => params.row.color.color,
      },
      {
        field: "relatedColor.system",
        label: "Related Color System",
        width: 150,
        type: "singleSelect",
        valueOptions: COLOR_SYSTEMS,
        valueGetter: (params: any) => params.row.relatedColor.system,
        renderCell: (params: any) =>
          params.value ? <SystemChip system={params.value} /> : null,
      },
      {
        field: "relatedColor.color",
        label: "Related Color",
        flex: 1,
        valueGetter: (params: any) => params.row.relatedColor.color,
      },
      {
        field: "confirmedByData",
        label: "Confirmed by data",
        width: 150,
        type: "boolean",
      },
    ],
    // A read-only user sees the relationships, and deletes none.
    getActions: (params: GraphQLActionParams) =>
      readonly
        ? []
        : [
            <GridActionsCellItem
              key="delete"
              icon={<DeleteOutlineIcon />}
              label={labels.deleteAction}
              sx={gridDeleteSx}
              onClick={() => handleDelete(params.row, params.refetch)}
            />,
          ],
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Adding is for those who may change the colors; a read-only user only reads them. */}
      {!readonly && (
        <Box>
          <SectionTitle>{labels.addTitle}</SectionTitle>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={sortedColors}
              loading={colorsLoading}
              getOptionLabel={getColorLabel}
              value={color}
              onChange={(_, val) => setColor(val)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <SystemChip system={option.system} />
                  {option.color}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Color" size="small" />
              )}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              sx={{ minWidth: 260 }}
              options={sortedColors.filter(
                (c) =>
                  c.id !== color?.id &&
                  !relatedColors.some((r) => r.id === c.id) &&
                  !existingRelatedIds.has(c.id),
              )}
              loading={colorsLoading}
              getOptionLabel={getColorLabel}
              value={relatedColors}
              onChange={(_, val) => setRelatedColors(val)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <SystemChip system={option.system} />
                  {option.color}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Related Colors" size="small" />
              )}
            />
            <ActionButton
              onClick={handleAdd}
              disabled={!color || relatedColors.length === 0 || adding}
              startIcon={adding ? <CircularProgress size={16} /> : undefined}
              sx={{ alignSelf: "center" }}
            >
              {labels.addButton}
            </ActionButton>
          </Box>
        </Box>
      )}

      <Box>
        <SectionTitle>{labels.existingTitle}</SectionTitle>
        <ToggleButtonGroup
          exclusive
          size="small"
          sx={{ ...toggleSelectorSx, mb: 2 }}
          value={confirmationFilter}
          onChange={(_, value) =>
            value !== null && setConfirmationFilter(value)
          }
          aria-label="Confirmation filter"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="confirmed">Confirmed</ToggleButton>
          <ToggleButton value="unconfirmed">Unconfirmed</ToggleButton>
        </ToggleButtonGroup>
        <GraphQLDataGrid parameters={gridParameters} />
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.children}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ColorRelationshipTab;
